import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { X, Trash } from '@phosphor-icons/react';
import ImageGallery from '../ImageGallery/ImageGallery';
import './ImageGalleryOverlay.styles.css';
import RelativeDate from '@/components/common/RelativeDate';
import { Badge } from "@/components/ui/badge";

const ROLE_CLASS_MAP = {
  owner: {
    badge: 'bg-green-100 text-green-800',
    text: 'text-green-900',
  },
  admin: {
    badge: 'bg-green-100 text-green-800',
    text: 'text-green-900',
  },
  call_center: {
    badge: 'bg-blue-100 text-blue-800',
    text: 'text-blue-900',
  },
  reception: {
    badge: 'bg-blue-100 text-blue-800',
    text: 'text-blue-900',
  },
  field_agent: {
    badge: 'bg-yellow-100 text-yellow-800',
    text: 'text-yellow-900',
  },
  returns_manager: {
    badge: 'bg-red-100 text-red-800',
    text: 'text-red-900',
  },
  orders: {
    badge: 'bg-orange-100 text-orange-800',
    text: 'text-orange-900',
  },
  system: {
    badge: 'bg-gray-200 text-gray-800',
    text: 'text-gray-700',
  },
  unknown: {
    badge: 'bg-gray-200 text-gray-800',
    text: 'text-gray-700',
  },
};

// Ensure thumbnails use small images instead of large
const ensureSmallThumbnail = (url) => {
  if (!url) return url;
  return url.replace('/large/', '/small/');
};

const ImageGalleryOverlay = ({ isOpen, onClose, images, initialIndex = 0, onDeleteImage }) => {
  const overlayRef = useRef(null);
  const galleryContentRef = useRef(null);
  // Track if user is interacting with zoomed image
  const [isInteractingWithZoomed, setIsInteractingWithZoomed] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [rotationAngles, setRotationAngles] = useState([]);

  // Process images to ensure thumbnails use small versions
  const processedImages = useMemo(() => {
    if (!Array.isArray(images)) return [];
    return images.map((img) => ({
      ...img,
      thumbnail: ensureSmallThumbnail(img.thumbnail || img.url),
    }));
  }, [images]);

  const currentMeta = useMemo(() => {
    const cur = processedImages?.[currentIndex] || {};
    // We allow metadata to be passed either at top-level or nested under meta
    return {
      created_at: cur.created_at || cur.meta?.created_at,
      created_by_username: cur.created_by_username || cur.meta?.created_by_username,
      created_by_role: cur.created_by_role || cur.meta?.created_by_role,
    };
  }, [processedImages, currentIndex]);

  // Handle scroll locking via body class (prevents scroll without resetting position)
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overlay-open');
      return () => {
        // document.body is null once the document starts tearing down (e.g. the
        // session-expired full-page redirect), and this cleanup can run then.
        if (document.body) document.body.classList.remove('overlay-open');
      };
    }
    document.body.classList.remove('overlay-open');
  }, [isOpen]);

  // Handle escape key to close gallery
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        e.stopPropagation(); // keep a drawer beneath the gallery open
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [isOpen, onClose]);

  // Handle click outside content to close
  const handleClick = (e) => {
    // Check if click is on the overlay or gallery content but not on the image itself
    if (!isOpen || isInteractingWithZoomed) return;
    
    // Find the actual image element
    const imageElement = e.target.closest('.main-image');
    const isSlider = e.target.closest('.gallery-controls');
    const isThumbnail = e.target.closest('.thumbnails-container');
    const isNavButton = e.target.closest('.gallery-nav-button');
    const closeButton = e.target.closest('.gallery-close-button');
    
    // If we clicked outside the image, close button, controls, thumbnails, or nav buttons, then close
    if (!imageElement && !isSlider && !isThumbnail && !isNavButton && !closeButton) {
      onClose();
    }
  };
  
  // Handle swipe gestures for mobile
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchTimeRef = useRef(0);
  
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
      touchTimeRef.current = Date.now();
    }
  };
  
  const handleTouchEnd = (e) => {
    if (!touchStartRef.current || isInteractingWithZoomed) return;
    
    const touchDuration = Date.now() - touchTimeRef.current;
    // Only process quick swipes (less than 300ms)
    if (touchDuration > 300) return;
    
    const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x;
    const deltaY = e.changedTouches[0].clientY - touchStartRef.current.y;
    
    // Check for horizontal swipe (greater horizontal than vertical movement)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      // This is handled by the ImageGallery component for navigation
      return;
    }
    
    // Check for vertical swipe down with sufficient distance
    if (deltaY > 100) {
      onClose();
    }
  };

  // Listen for zoomed interaction events from the ImageGallery
  const handleZoomedInteractionStart = () => {
    setIsInteractingWithZoomed(true);
  };

  const handleZoomedInteractionEnd = () => {
    setIsInteractingWithZoomed(false);
  };

  useEffect(() => {
    if (isOpen && processedImages.length > 0) {
      setRotationAngles(processedImages.map(() => 0));
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, processedImages, initialIndex]);

  const rotateCurrentImage = (delta) => {
    if (processedImages.length === 0) {
      return;
    }
    setRotationAngles((prev) => {
      const next = processedImages.map((_, index) => (prev && index < prev.length ? prev[index] || 0 : 0));
      const safeIndex = Math.max(0, Math.min(currentIndex, processedImages.length - 1));
      next[safeIndex] = (next[safeIndex] + delta + 360) % 360;
      return next;
    });
  };

  const handleRotateLeft = () => {
    rotateCurrentImage(-90);
  };

  const handleRotateRight = () => {
    rotateCurrentImage(90);
  };

  const currentRotation = rotationAngles[currentIndex] || 0;

  if (!isOpen || processedImages.length === 0) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className="gallery-overlay"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="gallery-overlay-content" ref={galleryContentRef}>
        {typeof onDeleteImage === 'function' && (
          <button
            className="gallery-delete-button"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteImage(processedImages[currentIndex], currentIndex);
            }}
            aria-label="Delete image"
            title="Șterge imaginea"
          >
            <Trash size={20} weight="bold" />
          </button>
        )}
        <button
          className="gallery-close-button"
          onClick={onClose}
          aria-label="Close gallery"
        >
          <X size={24} weight="bold" />
        </button>
        <ImageGallery
          images={processedImages}
          initialIndex={initialIndex}
          maxZoom={8}
          onZoomedInteractionStart={handleZoomedInteractionStart}
          onZoomedInteractionEnd={handleZoomedInteractionEnd}
          showNavButtons={true}
          onIndexChange={setCurrentIndex}
          rotation={currentRotation}
          onRotateLeft={handleRotateLeft}
          onRotateRight={handleRotateRight}
          renderCaption={() => (
            Boolean(currentMeta?.created_by_username || currentMeta?.created_at) && (
              <Badge
                variant="secondary"
                className={`text-xs px-1.5 py-px h-auto whitespace-nowrap font-normal pointer-events-auto ${
                  // dezshop.bot is stamped 'listing' on some write paths → gray like in Comments
                  (currentMeta?.created_by_username?.trim().toLowerCase() === 'dezshop.bot'
                    ? ROLE_CLASS_MAP.unknown.badge
                    : ROLE_CLASS_MAP[currentMeta?.created_by_role]?.badge) || ROLE_CLASS_MAP.unknown.badge
                }`}
              >
                {currentMeta?.created_by_username || 'client'}
                {currentMeta?.created_at && (
                  <span className="ml-1">
                    <RelativeDate date={currentMeta.created_at} />
                  </span>
                )}
              </Badge>
            )
          )}
        />
      </div>
    </div>,
    document.body
  );
};

ImageGalleryOverlay.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      thumbnail: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['image', 'pdf', 'video']),
      created_at: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      created_by_username: PropTypes.string,
      created_by_role: PropTypes.string,
    })
  ).isRequired,
  initialIndex: PropTypes.number,
  onDeleteImage: PropTypes.func,
};

export default ImageGalleryOverlay;
